import { getPage } from "./api.js";

/**
 * Loads a feed page by page as the reader reaches the end of it.
 *
 * A sentinel element sits after the list; when it scrolls into view the
 * next page is fetched. IntersectionObserver rather than a scroll
 * listener: the browser does the work off the main thread, and there is
 * nothing to throttle or to recompute on resize.
 *
 * The caller owns rendering. This owns *when* to ask for more, and the
 * guarantee that it only asks once at a time.
 */
export function infiniteScroll(container, {
  path,
  query = {},
  render,
  emptyMessage = "Nothing here yet.",
  /** Full markup for the empty state, when a plain sentence is not
   *  enough — the feed uses an illustrated panel. */
  emptyHtml = null,
  /** Called instead of the inline retry when the *first* page fails, so
   *  a view can show its own message (e.g. "sign in required"). */
  onError = null,
  /** Called once the server has nothing more, with how many were loaded: so a
   *  page can start the next list after this one (the Dive Events page's past
   *  events, after its upcoming ones). */
  onDone = null,
  limit,
}) {
  let cursor = null;
  let loading = false;
  let done = false;
  let loaded = 0;
  let observer = null;

  const list = document.createElement("div");
  const sentinel = document.createElement("div");
  // Given height so it can actually intersect; a zero-height element at
  // the very bottom of a page may never trigger.
  sentinel.style.minHeight = "1px";
  sentinel.dataset.role = "scroll-sentinel";

  const status = document.createElement("div");
  status.className = "text-center py-3";
  status.dataset.role = "scroll-status";

  container.innerHTML = "";
  container.append(list, sentinel, status);

  function setStatus(html) {
    status.innerHTML = html;
  }

  async function loadMore() {
    if (loading || done) return;
    loading = true;
    setStatus(`<div class="spinner-border spinner-border-sm text-primary"></div>`);
    try {
      const { items, nextCursor } = await getPage(path, { query, cursor, limit });
      cursor = nextCursor;
      loaded += items.length;
      if (items.length) render(list, items);
      // No cursor means the server has nothing after this page.
      if (!nextCursor) {
        done = true;
        observer?.disconnect();
        if (loaded === 0) {
          container.innerHTML = emptyHtml ?? `<p class="text-slate mb-0">${emptyMessage}</p>`;
        } else {
          setStatus("");
        }
        onDone?.(loaded);
      } else {
        setStatus("");
      }
    } catch (err) {
      // Stop observing so a failing endpoint is not retried on every
      // pixel of scroll; offer the reader the retry instead.
      observer?.disconnect();
      if (loaded === 0 && onError) {
        // Nothing on screen yet, so the view can own the whole message.
        loading = false;
        onError(err);
        return;
      }
      setStatus(
        `<button class="btn btn-sm btn-outline-secondary" data-role="scroll-retry">Could not load more — try again</button>`
      );
      status.querySelector("[data-role=scroll-retry]")?.addEventListener("click", () => {
        observer?.observe(sentinel);
        loadMore();
      });
    } finally {
      loading = false;
    }
  }

  // Not every environment has IntersectionObserver — jsdom does not.
  // Fall back to loading the first page and leaving a "load more"
  // button, which is strictly better than an empty list.
  if (typeof IntersectionObserver === "function") {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      // Start fetching a screen early, so the next page is usually
      // there by the time the reader arrives.
      { rootMargin: "400px" }
    );
    observer.observe(sentinel);
  }

  loadMore();

  return {
    /** Fetch the next page now. Exposed for tests and for the fallback. */
    loadMore,
    /** Stop watching. Views call this when they re-render. */
    destroy() {
      observer?.disconnect();
    },
    get state() {
      return { cursor, loading, done, loaded };
    },
  };
}
